import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';
import { UserInterface } from '../../models/user.interface';
import {
  PaymentHistoryItem,
  PaymentMethodDisplayResponse,
  UpgradePreviewResponse,
} from '../../models/payment.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { OneSignal } from 'onesignal-ngx';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  apiAddress: string = environment.apiAddress;

  private urlSubject: BehaviorSubject<string> = new BehaviorSubject<string>(''); // URL inicial vacía
  public url$: Observable<string> = this.urlSubject.asObservable();

  private nameSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public name$: Observable<string> = this.nameSubject.asObservable();

  private userSubject: BehaviorSubject<UserInterface | undefined> = new BehaviorSubject<UserInterface | undefined>(undefined);
  public user$: Observable<UserInterface | undefined> = this.userSubject.asObservable();
  constructor(
    private http: HttpClient,
    private readonly router: Router,
    private readonly cookieService: CookieService,
    private oneSignal: OneSignal,
    private logger: LoggerService
  ) {}
  setUser(user: UserInterface): void {
    localStorage.setItem('user', JSON.stringify(user));
    this.userSubject.next(user);
  }

  changeUrl(newUrl: string): void {
    this.urlSubject.next(newUrl);
    const user = this.getUserLocalStorage();
    if (user) {
      user.picture = newUrl;
      this.setUser(user);
    }
  }
  changeName(newName: string): void {
    this.nameSubject.next(newName);
    const user = this.getUserLocalStorage();
    if (user) {
      user.name = newName;
      this.setUser(user);
    }
  }
  getUser() {
    return this.http.get<UserInterface>(`${this.apiAddress}/user`);
  }
  updateUser(user: UserInterface) {
    return this.http.put<UserInterface>(`${this.apiAddress}/user`, user);
  }

  logOutService() {
    return this.http.get<UserInterface>(`${this.apiAddress}/logout`);
  }

  getUserLocalStorage(): UserInterface | undefined {
    const userStr = localStorage.getItem('user');
    if (!userStr) return undefined;
    const user: UserInterface = JSON.parse(userStr);
    return user;
  }
  createSubscription(subType: string, formData: any) {
    return this.http.post(`${this.apiAddress}/subscription/${subType}`, formData);
  }
  updatePassword(token: string, password: string, create: boolean) {
    const url = `${this.apiAddress}/user/password`;
    const body = { token, password: password, create };

    return this.http.post(url, body);
  }
  registerUser(name: string, email: string, inviteToken?: string) {
    const body: any = { name, email };
    if (inviteToken) {
      body.invite_token = inviteToken;
    }
    return this.http.post<any>(`${this.apiAddress}/user`, body);
  }
  loginUser(email: string, password: string) {
    return this.http.post<any>(`${this.apiAddress}/login/manual`, { email, password });
  }
  forgotPassword(email: string) {
    return this.http.post<any>(`${this.apiAddress}/user/forgot-password`, { email });
  }
  uploadImage(picture: FormData): Observable<any> {
    return this.http.post(`${this.apiAddress}/user/upload/picture`, picture);
  }
  deleteUser() {
    return this.http.delete(`${this.apiAddress}/user/account/delete`);
  }

  updateAcquisitionSource(source: string) {
    return this.http.patch(`${this.apiAddress}/user/acquisition-source`, { source });
  }

  updateLocalAcquisitionSource(source: string): void {
    const user = this.getUserLocalStorage();
    if (user) {
      user.acquisition_source = source;
      this.setUser(user);
    }
  }
  getMessageCount() {
    return this.http.get<any>(`${this.apiAddress}/user/account/messages/count`);
  }
  cancelSubscription() {
    return this.http.post(`${this.apiAddress}/subscription/cancel`, {});
  }

  /** Reactivate an expired subscription using saved payment method (no proration). */
  reactivate(newPlanId: number) {
    return this.http.post<object>(`${this.apiAddress}/subscription/reactivate`, {
      new_plan_id: newPlanId,
    });
  }

  /** Preview prorated amount for upgrading to newPlanId (e.g. 3=BASE, 4=PREMIUM). */
  getUpgradePreview(newPlanId: number) {
    return this.http.get<UpgradePreviewResponse>(
      `${this.apiAddress}/subscription/upgrade-preview?new_plan_id=${newPlanId}`
    );
  }

  /** Execute prorated upgrade; charges immediately. */
  upgrade(newPlanId: number) {
    return this.http.post<object>(`${this.apiAddress}/subscription/upgrade`, {
      new_plan_id: newPlanId,
    });
  }

  /** Schedule downgrade at end of current period. */
  scheduleDowngrade(newPlanId: number) {
    return this.http.post<{
      effective_date?: string;
      message?: string;
      new_plan_id?: number;
      new_plan_name?: string;
    }>(`${this.apiAddress}/subscription/schedule-downgrade`, {
      new_plan_id: newPlanId,
    });
  }

  /** Revert scheduled downgrade. */
  revertScheduledDowngrade() {
    return this.http.post<{ message?: string }>(`${this.apiAddress}/subscription/revert-scheduled-downgrade`, {});
  }

  getPaymentHistory() {
    return this.http.get<{ payments: PaymentHistoryItem[] }>(
      `${this.apiAddress}/subscription/payments`
    );
  }
  getPaymentMethod() {
    return this.http.get<PaymentMethodDisplayResponse>(
      `${this.apiAddress}/subscription/payment-method`
    );
  }
  deletePaymentMethod() {
    return this.http.delete<{ success?: boolean }>(
      `${this.apiAddress}/subscription/payment-method`
    );
  }
  deletePictureUser() {
    return this.http.delete(`${this.apiAddress}/user/delete/picture`);
  }

  logout() {
    this.logOutService().subscribe({
      next: (_value) => {
        localStorage.removeItem('user');
        localStorage.removeItem('current_workspace_id');
        this.userSubject.next(undefined);
        this.oneSignal.logout();
        this.router.navigate(['dashboard/login']);
      },
      error: (err) => {
        this.logger.error('No se pudo cerrar sesión', err);
      },
    });
  }
}
